import React, { useState } from 'react';
import { Eye, Trash2, CheckCircle, AlertTriangle, Users, Calendar, Clock, Edit2, Check, X as XIcon } from 'lucide-react';
import type { Project, User, TeamName } from '../types/core';
import { useAuth } from '../contexts/AuthContext'; // Assuming useAuth is here
import type { LanguageContextType } from '../translations'; // For the 't' function type

// Add function to calculate actual total received from fund entries
const calculateActualTotalReceived = (project: Project): number => {
  // Calculate from fund entries to ensure accuracy
  // Total = initial_project entries + additional_project entries + additional_to_user entries
  // Note: allocated_to_user entries are internal transfers and should NOT be counted

  const totalFromInitialEntries = (project.fundEntries || [])
    .filter(entry => entry.type === 'initial_project')
    .reduce((sum, entry) => sum + entry.amount, 0);

  const totalFromAdditionalProject = (project.fundEntries || [])
    .filter(entry => entry.type === 'additional_project')
    .reduce((sum, entry) => sum + entry.amount, 0);

  const totalFromAdditionalUsers = (project.purchaserFunds || []).reduce((sum, fund) => {
    return sum + (fund.fundEntries || [])
      .filter(entry => entry.type === 'additional_to_user')
      .reduce((entrySum, entry) => entrySum + entry.amount, 0);
  }, 0);

  const calculatedTotal = totalFromInitialEntries + totalFromAdditionalProject + totalFromAdditionalUsers;

  // If calculated total is greater than 0, use it
  if (calculatedTotal > 0) {
    return calculatedTotal;
  }

  // Fallback: use totalReceived if it exists
  if (project.totalReceived && project.totalReceived > 0) {
    return project.totalReceived;
  }

  // Final fallback for older projects without fund entries: use initialFund
  if (project.initialFund && project.initialFund > 0) {
    return project.initialFund;
  }

  return 0;
};

export interface ProjectCardProps {
  project: Project;
  onView: (project: Project) => void;
  onDelete: (project: Project) => void;
  currentUser: User | null;
  t: LanguageContextType['t'];
  isOverdue: (deadline?: string) => boolean;
  isDeadlineApproaching: (deadline?: string, days?: number) => boolean;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onView, onDelete, currentUser, t, isOverdue, isDeadlineApproaching, setProjects }) => {
  const { getSupervisorsByTeams } = useAuth();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(project.name);

  // Debug problematic project data
  if (project.name && project.name.includes('Thonburi')) {
  }
  // Helper function to calculate spent amount consistently with ProjectView
  const calculateRecursiveActualPrice = (item: any, allItems: any[], statusFilter?: string): number => {
    if (item.isGroup) {
      const children = allItems.filter((child: any) => child.parentId === item.id);

      // Check if groupTotalPrice is set - use it instead of summing children
      if (item.groupTotalPrice && item.groupTotalPrice > 0) {
        // Check if group has qualifying children
        const qualifyingChildren = children.filter((child: any) => {
          if (statusFilter === 'approved_or_unsupervised') {
            return child.status === 'approved' || child.status === 'bought' || child.isUnsupervisedPurchase === true;
          }
          return true;
        });

        if (qualifyingChildren.length > 0) {
          return item.groupTotalPrice + (item.shippingCost || 0);
        }
        return 0; // Group with no qualifying children contributes 0
      }

      // No groupTotalPrice set - sum children's prices as before
      const childrenTotalActualPrice = children.reduce((sum, child) => sum + calculateRecursiveActualPrice(child, allItems, statusFilter), 0);

      // For groups: only include the group's cost if it has children that meet the status filter
      // This ensures groups are only counted when they have relevant approved/unsupervised children
      if (childrenTotalActualPrice > 0) {
        return childrenTotalActualPrice + (item.shippingCost || 0);
      }
      return 0; // Group with no qualifying children contributes 0
    }

    // Apply status filter if provided for non-group items
    if (statusFilter === 'approved_or_unsupervised') {
      if (!(item.status === 'approved' || item.status === 'bought' || item.isUnsupervisedPurchase === true)) {
        return 0;
      }
    }
    // For individual items, include their shipping cost.
    return ((item.price || 0) * item.quantity) + (item.shippingCost || 0);
  };

  const totalSpent = project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0);
  const actualTotalReceived = calculateActualTotalReceived(project);
  const budgetUsage = actualTotalReceived > 0 ? (totalSpent / actualTotalReceived) * 100 : (totalSpent > 0 ? 100 : 0);

  // Filter out group items for these counts
  const approvedItems = project.items.filter(i => !i.isGroup && i.status === 'approved').length;
  const pendingItems = project.items.filter(i => !i.isGroup && i.status === 'pending').length;

  const involvedSupervisors = getSupervisorsByTeams(project.teamsInvolved);
  const requiredDeletionApprovals = involvedSupervisors.length;
  const currentDeletionApprovals = project.deletionApprovals?.length || 0;
  const currentUserHasApprovedDeletion = project.deletionApprovals?.some(app => app.supervisorId === currentUser?.id);

  const canInitiateDeletion = currentUser && (currentUser.role === 'admin' || (currentUser.role === 'supervisor' && project.teamsInvolved.includes(currentUser.team))) && project.status === 'active';
  const canApproveDeletion = currentUser && currentUser.role === 'supervisor' && project.teamsInvolved.includes(currentUser.team) && project.status === 'pending_deletion' && !currentUserHasApprovedDeletion;
  const canDirectDeleteByAdmin = currentUser && currentUser.role === 'admin' && (project.status === 'active' || (project.status === 'pending_deletion' && requiredDeletionApprovals <= 1));

  let deleteButtonAction: 'initiate' | 'approve' | 'delete_direct' | 'none' = 'none';
  let deleteButtonText = t('action.delete');
  let deleteButtonTitle = t('project.delete');
  let deleteButtonIcon = <Trash2 className="w-4 h-4" />;
  let deleteButtonClasses = "p-1 text-gray-500 hover:text-red-600 transition-colors";

  if (project.status === 'pending_deletion') {
    deleteButtonText = `${t('project.delete.approve')} (${currentDeletionApprovals}/${requiredDeletionApprovals})`;
    deleteButtonTitle = t('project.delete.approve');
    deleteButtonIcon = <CheckCircle className="w-4 h-4" />;
    deleteButtonClasses = "p-1 text-orange-500 hover:text-orange-700 transition-colors";
    if (currentUserHasApprovedDeletion) {
      deleteButtonText = t('project.delete.alreadyApproved');
      deleteButtonTitle = t('project.delete.alreadyApproved');
      deleteButtonClasses = "p-1 text-green-500 cursor-not-allowed";
      deleteButtonIcon = <CheckCircle className="w-4 h-4 text-green-500" />;
    } else if (canApproveDeletion) {
      deleteButtonAction = 'approve';
    } else {
      deleteButtonClasses = "p-1 text-gray-400 cursor-not-allowed";
    }
  } else if (project.status === 'active') {
    if (canDirectDeleteByAdmin) {
      deleteButtonAction = 'delete_direct';
      deleteButtonText = t('action.delete');
      deleteButtonTitle = t('project.delete');
    } else if (canInitiateDeletion) {
      deleteButtonAction = 'initiate';
      deleteButtonText = t('project.delete.initiate');
      deleteButtonTitle = t('project.delete.initiate');
      deleteButtonClasses = "p-1 text-yellow-600 hover:text-yellow-700 transition-colors";
      deleteButtonIcon = <AlertTriangle className="w-4 h-4" />;
    }
  }

  const showDeleteButton = project.status !== 'closed' && (deleteButtonAction !== 'none' || (project.status === 'pending_deletion' && (currentUser?.role === 'admin' || currentUserHasApprovedDeletion)));

  // Check if user can edit project name (admin or supervisor involved in project)
  const canEditProjectName = currentUser &&
    (currentUser.role === 'admin' ||
      (currentUser.role === 'supervisor' && project.teamsInvolved.includes(currentUser.team)));

  const handleSaveProjectName = async () => {
    if (!editedName.trim() || editedName === project.name) {
      setIsEditingName(false);
      setEditedName(project.name);
      return;
    }

    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      const { removeUndefinedValues } = await import('../utils');

      const updatedProject = { ...project, name: editedName.trim() };
      const projectDocRef = doc(db, 'projects', project.id.toString());
      await setDoc(projectDocRef, removeUndefinedValues(updatedProject));

      // Update local state
      setProjects(prevProjects =>
        prevProjects.map(p => p.id === project.id ? updatedProject : p)
      );

      setIsEditingName(false);
    } catch (error) {
      alert(t('error.generic', { defaultValue: 'Failed to update project name.' }));
      setEditedName(project.name);
      setIsEditingName(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(project.name);
    setIsEditingName(false);
  };

  return (
    <div onClick={() => onView(project)} className="project-card card card-clickable project-card-border project-card-border-hover p-6">
      <div className="project-card-header">
        <div className="project-card-title-section">
          {/* Status badge row */}
          <div className="flex items-center mb-2">
            <span className={`project-status-pill ${project.status === 'active' ? 'project-status-active' :
              project.status === 'closed' ? 'project-status-closed' :
                project.status === 'pending_deletion' ? 'project-status-pending-deletion' : ''
              }`}>{t(`project.status.${project.status}`)}</span>
          </div>
          {/* Project title row */}
          <div className="mb-2 flex items-center gap-2 min-w-0">
            {isEditingName ? (
              <>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveProjectName();
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                  className="flex-1 min-w-0 px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg font-bold"
                  autoFocus
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveProjectName();
                  }}
                  className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors flex-shrink-0"
                  title={t('action.save', { defaultValue: 'Save' })}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                  title={t('action.cancel', { defaultValue: 'Cancel' })}
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <h3 className="project-card-title truncate flex-1 min-w-0">{project.name}</h3>
                {canEditProjectName && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingName(true);
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                    title={t('action.edit', { defaultValue: 'Edit' })}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
          <p className="project-card-description line-clamp-2">{project.description}</p>
        </div>
        {showDeleteButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (deleteButtonAction === 'initiate' || deleteButtonAction === 'approve' || deleteButtonAction === 'delete_direct') {
                onDelete(project);
              }
            }}
            className={deleteButtonClasses}
            title={deleteButtonTitle}
            disabled={deleteButtonAction === 'none' || (project.status === 'pending_deletion' && currentUserHasApprovedDeletion && !canApproveDeletion && currentUser?.role !== 'admin')}
          >
            {deleteButtonIcon}
            {(project.status === 'pending_deletion' || deleteButtonAction === 'initiate') && <span className="ml-1 text-xs">{deleteButtonText}</span>}
          </button>
        )}
      </div>
      <div className="project-card-budget-info">
        <div className="project-card-budget-text">
          <span className="project-card-budget-label">Budget Usage</span>
          <span className={`project-card-budget-value ${actualTotalReceived === 0 && totalSpent > 0 ? 'text-red-600' : ''}`}>
            ฿{totalSpent.toLocaleString()} / ฿{actualTotalReceived.toLocaleString()}
            {actualTotalReceived === 0 && totalSpent > 0 && <span className="ml-1 text-red-600 text-xs">⚠️ No Budget</span>}
          </span>
        </div>
        <div className="project-card-budget-bar-bg">
          <div className={`project-card-budget-bar-fg ${actualTotalReceived === 0 && totalSpent > 0 ? 'bg-red-500' :
            budgetUsage > 90 ? 'bg-simple-red-500' :
              budgetUsage > 70 ? 'bg-simple-yellow-500' :
                'bg-simple-green-500'
            }`} style={{ width: `${Math.min(budgetUsage, 100)}%` }} />
        </div>
        <div className={`project-card-budget-percentage ${actualTotalReceived === 0 && totalSpent > 0 ? 'text-red-600' : ''}`}>
          {actualTotalReceived === 0 && totalSpent > 0 ? 'Over Budget!' : `${budgetUsage.toFixed(1)}% used`}
        </div>
      </div>
      <div className="project-card-stats-grid">
        <div className="project-card-stat-item"><CheckCircle className="project-card-stat-icon text-green-600" /><div><div className="project-card-stat-text-lg">{approvedItems}</div><div className="project-card-stat-text-sm">Approved</div></div></div>
        <div className="project-card-stat-item"><Clock className="project-card-stat-icon text-yellow-600" /><div><div className="project-card-stat-text-lg">{pendingItems}</div><div className="project-card-stat-text-sm">Pending</div></div></div>
      </div>
      <div className="project-card-details-section space-y-2">
        <div className="project-card-teams-flex"><Users className="project-card-teams-icon" /><div className="project-card-teams-tags">{project.teamsInvolved.slice(0, 2).map((team: TeamName) => (<span key={team} className="project-card-team-tag project-card-team-tag-blue">{team}</span>))}{project.teamsInvolved.length > 2 && (<span className="project-card-team-tag project-card-team-tag-gray">+{project.teamsInvolved.length - 2} more</span>)}</div></div>
        {project.deadline && project.deadline !== '' && (<div className="project-card-deadline-flex"><Calendar className="project-card-deadline-icon" /><span className={`project-card-deadline-text ${isOverdue(project.deadline) ? 'project-card-deadline-overdue' : isDeadlineApproaching(project.deadline) ? 'project-card-deadline-warning' : 'project-card-deadline-normal'}`}>{isOverdue(project.deadline) ? 'Overdue: ' : isDeadlineApproaching(project.deadline) ? 'Due soon: ' : ''}{new Date(project.deadline).toLocaleDateString()}</span></div>)}
      </div>
      <div className="project-card-footer project-card-footer-border-top"><div className="project-card-footer-content"><div className="project-card-created-date">Created {project.createdAt && project.createdAt !== '' ? new Date(project.createdAt).toLocaleDateString() : 'No date'}</div><button onClick={(e) => { e.stopPropagation(); onView(project); }} className="project-card-view-button"><Eye className="project-card-view-button-icon" />View</button></div></div>
    </div>
  );
};

export default ProjectCard;
